import { ColumnTypes, ITableColumn } from '@makyoapp/makyo-sdk/models/Table'
import {
  ALL_FUNCTIONS_GROUP,
  ColumnAdapter,
  ConditionArrayOperator,
  ConditionBooleanOperator,
  ConditionColorOperator,
  ConditionContentOperator,
  ConditionDateOperator,
  ConditionEqualityOperator,
  ConditionFeatureFlag,
  ConditionNumericOperator,
  ConditionTextOperator,
  DATE_OPERATOR_WITH_RANGE_RHS,
  DATE_OPERATOR_WITH_RHS,
  DefaultConditionOperator,
  FunctionColOptionsOnly,
  getLookupDestinationTableColumn,
  NUMERIC_OPERATOR_WITH_INPUT_RHS,
  NUMERIC_OPERATOR_WITH_RANGE_RHS,
  TableAdapter,
} from '@makyoapp/makyo-common'

import { IDBTable } from '@/core/db'
import { getFnColumnSettings } from '@/core/db/functions/helpers'

/**
 * This function is used to check if a condition has a specific feature flag.
 */
export function hasConditionFeatureFlag(flags: ConditionFeatureFlag, lookup: ConditionFeatureFlag) {
  return (flags & lookup) === lookup
}

export function getOperatorOptions(
  col: ColumnAdapter,
  curTable: TableAdapter,
  tables: TableAdapter[]
): Record<string, string> {
  switch (col.type) {
    case ColumnTypes.Number:
    case ColumnTypes.Rating:
      return ConditionNumericOperator
    case ColumnTypes.Color:
      return ConditionColorOperator
    case ColumnTypes.DateTime:
      return ConditionDateOperator
    case ColumnTypes.Boolean:
      return ConditionBooleanOperator
    case ColumnTypes.Icon:
      return ConditionContentOperator
    case ColumnTypes.QRCode:
      return ConditionEqualityOperator
    case ColumnTypes.ImageArray:
      return ConditionContentOperator
    case ColumnTypes.Array:
      return ConditionArrayOperator
    case ColumnTypes.Lookup: {
      if (col.lookup?.standard && col.resolvedType) {
        return getOperatorOptions({ type: col.resolvedType } as ColumnAdapter, curTable, tables)
      }
      const { fTable, fCol } = getLookupDestinationTableColumn(col, curTable, tables)
      if (fCol && fTable) {
        return getOperatorOptions(fCol, fTable, tables)
      } else {
        return ConditionTextOperator
      }
    }
    case ColumnTypes.Function: {
      const fnSettings = getFnColumnSettings(col)
      if (fnSettings) {
        const theFn = ALL_FUNCTIONS_GROUP.find((f) => f.id === fnSettings.item.id)
        return getOperatorOptions(
          {
            type: theFn?.returnType || ColumnTypes.Text,
          } as ColumnAdapter,
          curTable,
          tables
        )
      } else {
        return ConditionTextOperator
      }
    }
    default:
      return ConditionTextOperator
  }
}

export function getOperatorDefaultOption(
  col: ColumnAdapter,
  curTable: TableAdapter,
  tables: TableAdapter[]
): DefaultConditionOperator {
  switch (col.type) {
    case ColumnTypes.Number:
    case ColumnTypes.Rating:
      return ConditionNumericOperator.IsNotEmpty
    case ColumnTypes.DateTime:
      return ConditionDateOperator.IsNotEmpty
    case ColumnTypes.Boolean:
      return ConditionBooleanOperator.IsTrue
    case ColumnTypes.Icon:
      return ConditionDateOperator.IsNotEmpty
    case ColumnTypes.QRCode:
      return ConditionEqualityOperator.IsEqual
    case ColumnTypes.ImageArray:
      return ConditionContentOperator.IsNotEmpty
    case ColumnTypes.Array:
      return ConditionArrayOperator.IsNotEmpty
    case ColumnTypes.Lookup: {
      const { fTable, fCol } = getLookupDestinationTableColumn(col, curTable, tables)
      if (fCol && fTable) {
        return getOperatorDefaultOption(fCol, fTable, tables)
      } else {
        return ConditionTextOperator.IsNotEmpty
      }
    }
    case ColumnTypes.Function: {
      const fnValue = (col.settings as FunctionColOptionsOnly).functionValue
      if (fnValue) {
        const theFn = ALL_FUNCTIONS_GROUP.find((f) => f.id === fnValue.item.id)
        return getOperatorDefaultOption(
          {
            type: theFn?.returnType || ColumnTypes.Text,
          } as ColumnAdapter,
          curTable,
          tables
        )
      } else {
        return ConditionTextOperator.IsNotEmpty
      }
    }
    case ColumnTypes.Color:
      return ConditionColorOperator.IsNotEmpty
    default:
      return ConditionTextOperator.IsNotEmpty
  }
}

// todo: add to makyo-common
const CUSTOM_TEXT_OPERATOR_WITH_RHS = [
  ConditionTextOperator.IsEqual,
  ConditionTextOperator.IsNotEqual,
  ConditionTextOperator.BeginsWith,
  ConditionTextOperator.EndsWith,
  ConditionTextOperator.Contains,
  ConditionTextOperator.DoesNotContain,
  ConditionTextOperator.IsContainedIn,
  ConditionTextOperator.IsNotContainedIn,
]

/**
 * Validate the rhs & rhsEnd values for the condition operator
 *
 * @todo check other operator types
 */
export function validateRhsEndValues(opt: DefaultConditionOperator): Record<string, any> {
  let extra = {}

  switch (true) {
    case CUSTOM_TEXT_OPERATOR_WITH_RHS.includes(opt as any):
      extra = {
        rhs: {
          isCustom: true,
          value: '',
        },
      }
      break
    case NUMERIC_OPERATOR_WITH_INPUT_RHS.includes(opt as any):
      extra = {
        rhs: {
          isCustom: true,
          value: '0',
        },
      }
      break
    case NUMERIC_OPERATOR_WITH_RANGE_RHS.includes(opt as any):
      extra = {
        rhs: {
          isCustom: true,
          value: '0',
        },
        rhsEnd: {
          isCustom: true,
          value: '0',
        },
      }
      break
    case DATE_OPERATOR_WITH_RHS.includes(opt as any):
      extra = {
        rhs: {
          isCustom: true,
          value: new Date().toISOString(),
        },
      }
      break
    case DATE_OPERATOR_WITH_RANGE_RHS.includes(opt as any):
      extra = {
        rhs: {
          isCustom: true,
          value: new Date().toISOString(),
        },
        rhsEnd: {
          isCustom: true,
          value: new Date().toISOString(),
        },
      }
      break
    default:
      extra = {
        rhs: null,
        rhsEnd: null,
      }
      break
  }

  return extra
}

/**
 * new utils to resolve the lookup column using raw idbTable
 */
export function getLookupResolvedColumn(
  col: ITableColumn,
  curTable: IDBTable,
  tablesMap: Map<string, IDBTable>
): ITableColumn | undefined {
  if (!col._Lookup) return

  const { _ConnectionColumnID, _ForeignColumnIDToShow } = col._Lookup

  const connCol = curTable.columnsMap.get(_ConnectionColumnID)
  if (!connCol) return

  // source column to match the data
  const sMatchCol = curTable.columnsMap.get(_ConnectionColumnID)
  if (!sMatchCol) return

  // Foreign Table
  const fTable = tablesMap.get(connCol._Connection._ForeignTableID!)
  if (!fTable) return

  // Foreign Column to match it's data
  const fMatchCol = fTable.columnsMap.get(connCol._Connection._ForeignColumnID!)
  if (!fMatchCol) return

  // Foreign Column to show it's data
  const fCol = fTable.columnsMap.get(_ForeignColumnIDToShow)
  if (!fCol) return

  return fCol
}
